import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    FaComments, FaRobot, FaHeadset, FaGlobeAmericas, FaMobileAlt, FaCodeBranch,
    FaCheckCircle, FaChevronDown, FaChevronUp, FaChevronRight, FaDatabase, FaMagic,
    FaMicrophone, FaSlack, FaWhatsapp, FaFacebookMessenger
} from 'react-icons/fa';
import heroImage from '../../assets/AI Images (4k) - Freepik (310724441653).png';

const ChatbotDevelopmentPage = () => {
    const [openFaq, setOpenFaq] = useState(null);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const services = [
        { icon: FaHeadset, title: 'Customer Support Bots', desc: 'Resolve 80% of common queries instantly without human intervention.', image: 'https://images.unsplash.com/photo-1535378437327-1e5820fb26e2?w=600' },
        { icon: FaComments, title: 'Conversational Sales', desc: 'Guide visitors through your funnel and qualify leads automatically.', image: 'https://images.unsplash.com/photo-1557426272-fc759fdf7a8d?w=600' },
        { icon: FaGlobeAmericas, title: 'Multilingual AI', desc: 'Support customers in 50+ languages with native-level fluency.', image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600' },
        { icon: FaMobileAlt, title: 'App Assistants', desc: 'Embed voice or text assistants directly into your mobile apps.', image: 'https://images.unsplash.com/photo-1555421689-d68471e189f2?w=600' },
        { icon: FaDatabase, title: 'Knowledge Base Bots', desc: 'Internal tools for employees to find documents and answers fast.', image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=600' },
        { icon: FaMagic, title: 'Voice AI', desc: 'Voice-enabled bots for call centers and smart devices.', image: 'https://images.unsplash.com/photo-1589254065878-42c9da997008?w=600' }
    ];

    const platforms = [
        { name: 'WhatsApp', icon: FaWhatsapp },
        { name: 'Messenger', icon: FaFacebookMessenger },
        { name: 'Slack', icon: FaSlack },
        { name: 'Website Widget', icon: FaGlobeAmericas },
        { name: 'Voice/IVR', icon: FaMicrophone },
        { name: 'Mobile App', icon: FaMobileAlt }
    ];

    // Horizontal Flow Steps (Conversation Design)
    const flowSteps = [
        { title: 'Persona', icon: FaRobot, color: 'from-green-500 to-emerald-600' },
        { title: 'Flows', icon: FaCodeBranch, color: 'from-teal-400 to-cyan-500' },
        { title: 'Integrate', icon: FaDatabase, color: 'from-blue-500 to-indigo-500' },
        { title: 'Train', icon: FaComments, color: 'from-purple-500 to-pink-500' },
        { title: 'Launch', icon: FaMagic, color: 'from-orange-500 to-red-500' },
        { title: 'Optimize', icon: FaCheckCircle, color: 'from-lime-500 to-green-600' }
    ];

    const faqs = [
        { q: 'Is it a rule-based bot or AI?', a: 'We build primarily AI-driven bots using NLP (Natural Language Processing) to deliver fluid, human-like conversations, but we can implement rule-based fallbacks for strict compliance.' },
        { q: 'Can it hand off to a human?', a: 'Yes. Our "Human-in-the-Loop" design ensures complex issues are seamlessly transferred to a live agent with full chat history.' },
        { q: 'Which platforms do you support?', a: 'All of them. Web, WhatsApp, Facebook Messenger, Slack, Microsoft Teams, Telegram, and even SMS.' },
        { q: 'How long does training take?', a: 'We can deploy a pre-trained model in ~2 weeks. Custom training on your specific domain data typically takes 4-6 weeks for high accuracy.' },
        { q: ' Is data secure?', a: 'Yes. We adhere to GDPR/CCPA standards. Interactions can be anonymized, and sensitive PII can be redacted automatically before storage.' }
    ];

    const whyChooseUs = [
        { title: 'Natural Conversations', desc: 'We design personalities, not just scripts. Our bots feel human.' },
        { title: 'Context Awareness', desc: 'Our bots remember past interactions and user preferences.' },
        { title: 'Omnichannel', desc: 'Build once, deploy everywhere. Consistent experience across web and mobile.' },
        { title: 'Deep Integration', desc: 'We hook into your CRM (Salesforce, HubSpot) to personalize every chat.' },
        { title: 'Sentiment Analysis', desc: 'The bot detects frustration and escalates to a human immediately.' },
        { title: 'Continuous Learning', desc: 'The more it chats, the smarter it gets. We set up feedback loops.' }
    ];

    return (
        <div className="bg-white font-sans">
            {/* Hero Section */}
            <section className="relative h-[60vh] min-h-[500px] overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url('${heroImage}')` }}
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/95 via-green-900/90 to-transparent"></div>
                </div>

                <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex items-center pt-20">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-3xl text-white"
                    >
                        <p className="text-xs font-light tracking-widest uppercase text-emerald-300 mb-4">Engagement &gt; Automation</p>
                        <h1 className="text-5xl md:text-6xl font-light mb-6 leading-tight">Intelligent <br /><span className="text-emerald-400 font-normal">Chatbots</span></h1>
                        <p className="text-lg font-light text-emerald-100 leading-relaxed mb-8 max-w-2xl">
                            Conversations that convert. Deliver 24/7 instant support and personalized experiences at scale.
                        </p>
                        <div className="flex flex-wrap gap-4">
                            <a href="#inquiry" className="px-8 py-3 bg-emerald-600 text-white font-medium rounded-sm hover:bg-emerald-700 hover:shadow-lg transition-all text-sm uppercase tracking-wide">
                                Build A Bot
                            </a>
                            <a href="#services" className="px-8 py-3 border border-emerald-300 text-white font-medium rounded-sm hover:bg-white hover:text-emerald-900 transition-all text-sm uppercase tracking-wide">
                                See Demo
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Intro Stats */}
            <section className="py-24 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="max-w-4xl mx-auto text-center mb-20">
                        <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-6">Always On, Always Ready</h2>
                        <p className="text-lg text-slate-600 leading-relaxed">
                            Never miss a lead or leave a customer waiting again.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-12 text-center">
                        {[
                            { label: 'Query Resolution', val: '80%' },
                            { label: 'Response Time', val: '< 1s' },
                            { label: 'Cost Savings', val: '60%' },
                            { label: 'Customer CSAT', val: '4.8/5' }
                        ].map((stat, idx) => (
                            <div key={idx} className="relative p-6 group">
                                <div className="absolute inset-0 bg-white rounded-sm shadow-sm transform transition-transform group-hover:-translate-y-2 group-hover:shadow-md"></div>
                                <div className="relative z-10">
                                    <div className="text-5xl font-bold text-emerald-600 mb-3">{stat.val}</div>
                                    <div className="text-sm text-slate-500 uppercase tracking-widest">{stat.label}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Grid (Hover Cards) */}
            <section id="services" className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-4">Conversational Solutions</h2>
                        <p className="text-lg text-slate-600">
                            More than just a chat bubble. A full virtual assistent.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {services.map((service, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="group relative h-80 overflow-hidden rounded-sm bg-slate-900 cursor-pointer"
                            >
                                <div className="absolute inset-0 bg-cover bg-center opacity-60 transition-transform duration-700 group-hover:scale-110 group-hover:opacity-40" style={{ backgroundImage: `url(${service.image})` }}></div>
                                <div className="absolute inset-0 bg-gradient-to-t from-green-900 via-transparent to-transparent opacity-90"></div>

                                <div className="absolute inset-0 p-8 flex flex-col justify-end z-10">
                                    <div className="transform transition-transform duration-500 group-hover:-translate-y-4">
                                        <service.icon className="text-4xl text-emerald-400 mb-4" />
                                        <h3 className="text-2xl font-light text-white mb-2">{service.title}</h3>
                                        <div className="h-0 overflow-hidden group-hover:h-auto transition-all duration-500">
                                            <p className="text-emerald-100 text-sm leading-relaxed mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">{service.desc}</p>
                                        </div>
                                    </div>
                                    <div className="w-12 h-1 bg-emerald-500 mt-4 transform origin-left transition-all duration-500 group-hover:w-full"></div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Tech Stack */}
            <section className="py-20 bg-slate-50 border-y border-slate-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <p className="text-center text-sm font-bold text-slate-400 uppercase tracking-widest mb-10">Deploy Anywhere</p>
                    <div className="flex flex-wrap justify-center gap-8 md:gap-16 opacity-70 grayscale hover:grayscale-0 transition-all duration-500">
                        {platforms.map((tech, idx) => (
                            <div key={idx} className="flex flex-col items-center gap-2 group cursor-default">
                                <tech.icon className="text-4xl text-slate-600 group-hover:text-emerald-600 transition-colors" />
                                <span className="text-sm font-medium text-slate-500 group-hover:text-slate-800">{tech.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Timeline (Implementation Process) */}
            <section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-20">
                        <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-4">Conversation Design</h2>
                        <p className="text-slate-600">Bot Logic that feels human.</p>
                    </div>

                    <div className="relative">
                        {/* Base Line (Grey) */}
                        <div className="hidden md:block absolute top-[40px] left-0 w-full h-0.5 bg-slate-200 -z-0"></div>

                        {/* Steps Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-y-12 gap-x-4 relative">
                            {flowSteps.map((step, idx) => (
                                <div key={idx} className="relative flex flex-col items-center group">
                                    {/* Circle Icon */}
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.5 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: idx * 0.1, type: "spring", stiffness: 100 }}
                                        className="relative z-20 mb-6"
                                    >
                                        <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center text-white text-2xl shadow-xl group-hover:scale-110 transition-transform duration-300 ring-4 ring-white`}>
                                            <step.icon />
                                        </div>
                                    </motion.div>

                                    {/* Title */}
                                    <h3 className="text-lg font-bold text-slate-800 text-center">{step.title}</h3>

                                    {/* Mobile Connector */}
                                    {idx < flowSteps.length - 1 && (
                                        <div className="md:hidden absolute -bottom-8 left-1/2 w-0.5 h-8 bg-slate-200"></div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="py-24 bg-emerald-900 text-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid md:grid-cols-2 gap-16 items-center">
                        <div>
                            <p className="text-emerald-400 font-bold tracking-widest uppercase mb-4">Why Tech-Inovant Bots</p>
                            <h2 className="text-4xl md:text-5xl font-light mb-8 leading-tight">Empathy <br />Engineered</h2>
                            <p className="text-emerald-100 text-lg leading-relaxed mb-8">
                                Most bots are annoying. Ours aren't. We obsess over "conversation design" to ensure users feel heard, understood, and helped.
                            </p>
                            <a href="#inquiry" className="inline-flex items-center text-emerald-400 font-medium hover:text-white transition-colors">
                                Try a Demo Bot <FaChevronRight className="ml-2 text-xs" />
                            </a>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-6">
                            {whyChooseUs.map((item, idx) => (
                                <div key={idx} className="bg-emerald-800/50 p-6 rounded-sm border border-emerald-700 hover:border-green-500/50 transition-colors">
                                    <FaCheckCircle className="text-green-500 text-xl mb-3" />
                                    <h3 className="text-lg font-medium mb-2">{item.title}</h3>
                                    <p className="text-emerald-200 text-sm leading-relaxed">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ & Inquiry Form */}
            <section id="inquiry" className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid lg:grid-cols-2 gap-16">

                        {/* FAQs */}
                        <div>
                            <h2 className="text-3xl font-light text-slate-900 mb-8">Chatbot FAQs</h2>
                            <div className="space-y-4">
                                {faqs.map((faq, idx) => (
                                    <div key={idx} className="border-b border-slate-200">
                                        <button
                                            onClick={() => toggleFaq(idx)}
                                            className="w-full py-6 text-left flex justify-between items-center group"
                                        >
                                            <span className="text-lg text-slate-800 font-light group-hover:text-emerald-600 transition-colors">{faq.q}</span>
                                            {openFaq === idx ? <FaChevronUp className="text-emerald-500" /> : <FaChevronDown className="text-slate-300" />}
                                        </button>
                                        <motion.div
                                            initial={false}
                                            animate={{ height: openFaq === idx ? 'auto' : 0, opacity: openFaq === idx ? 1 : 0 }}
                                            className="overflow-hidden"
                                        >
                                            <p className="pb-6 text-slate-500 leading-relaxed">{faq.a}</p>
                                        </motion.div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Inquiry Form */}
                        <div className="bg-slate-50 p-10 rounded-sm border border-slate-200">
                            <h2 className="text-3xl font-light text-slate-900 mb-2">Automate Support</h2>
                            <p className="text-slate-500 mb-8">Deploy your first AI agent in weeks.</p>

                            <form className="space-y-6">
                                <div className="grid grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Name</label>
                                        <input type="text" className="w-full bg-white px-4 py-3 border-b-2 border-slate-200 focus:border-emerald-500 outline-none transition-colors" placeholder="Jane Doe" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Company</label>
                                        <input type="text" className="w-full bg-white px-4 py-3 border-b-2 border-slate-200 focus:border-emerald-500 outline-none transition-colors" placeholder="Acme Inc." />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Email</label>
                                    <input type="email" className="w-full bg-white px-4 py-3 border-b-2 border-slate-200 focus:border-emerald-500 outline-none transition-colors" placeholder="jane@acme.com" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Primary Channel</label>
                                    <select className="w-full bg-white px-4 py-3 border-b-2 border-slate-200 focus:border-emerald-500 outline-none transition-colors text-slate-700">
                                        <option>Website Chat</option>
                                        <option>WhatsApp / Messenger</option>
                                        <option>Mobile App</option>
                                        <option>Internal (Slack/Teams)</option>
                                    </select>
                                </div>

                                <button type="submit" className="w-full bg-emerald-600 text-white font-bold py-4 rounded-sm hover:bg-emerald-700 transition-all shadow-lg mt-4 uppercase tracking-widest text-sm">
                                    Get Bot Demo
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ChatbotDevelopmentPage;
